/** Permanent Audit File — from APM 2024 Edition PAF01–PAF13 sheets. */

import type {G0Page} from '@/pages/components/audit-program/g-section/constants';
import type {SectionIndexRow} from '@/pages/components/audit-program/SectionIndexView';
import type {AppendixTablePage} from '@/pages/components/audit-program/appendix/types';
import {PAF04_PAGE} from '@/pages/components/audit-program/paf-section/paf04';
import {PAF06_1_PAGE} from '@/pages/components/audit-program/paf-section/paf06_1';
import {PAF08_PAGE} from '@/pages/components/audit-program/paf-section/paf08';
import {PAF09_PAGE} from '@/pages/components/audit-program/paf-section/paf09';

export type PafSectionPageCode =
    | 'PAF01'
    | 'PAF02'
    | 'PAF03'
    | 'PAF04'
    | 'PAF05'
    | 'PAF06'
    | 'PAF06.1'
    | 'PAF06.2'
    | 'PAF07'
    | 'PAF08'
    | 'PAF09'
    | 'PAF10'
    | 'PAF11'
    | 'PAF13';

export const PAF_SECTION_PAGES: PafSectionPageCode[] = [
    'PAF01',
    'PAF02',
    'PAF03',
    'PAF04',
    'PAF05',
    'PAF06',
    'PAF06.1',
    'PAF06.2',
    'PAF07',
    'PAF08',
    'PAF09',
    'PAF10',
    'PAF11',
    'PAF13',
];

export const PAF_PAGE_TITLES: Record<PafSectionPageCode, string> = {
    PAF01: 'Permanent audit file index',
    PAF02: 'Background information',
    PAF03: 'Bankers and professional advisors',
    PAF04: 'Know your client checklist',
    PAF05: 'Register of laws and regulations',
    PAF06: 'Details of related parties',
    'PAF06.1': 'Related parties checklist (HKAS 24)',
    'PAF06.2': 'Related party transactions letter',
    PAF07: 'Significant accounting policies',
    PAF08: 'New client checklist',
    PAF09: 'Systems overview',
    PAF10: 'Register of non-assurance services',
    PAF11: 'Register of involvement in the audit',
    PAF13: 'Accounting estimates',
};

export const PAF_SECTION_INDEX_LETTER = 'PAF';
export const PAF_SECTION_INDEX_TITLE = 'PERMANENT AUDIT FILE';

export type PafIndexRow = SectionIndexRow & {
    pageCode?: Exclude<PafSectionPageCode, 'PAF01'>;
};

export const PAF_SECTION_INDEX: PafIndexRow[] = [
    {id: 's1', kind: 'section', num: '1', title: 'General information'},
    {id: 'i1.1', kind: 'item', num: '1.1', title: 'Background information', pageCode: 'PAF02'},
    {id: 'i1.2', kind: 'item', num: '1.2', title: 'Details of bankers and professional advisors', pageCode: 'PAF03'},
    {id: 'i1.3', kind: 'item', num: '1.3', title: 'Know your client checklist', pageCode: 'PAF04'},
    {id: 'i1.4', kind: 'item', num: '1.4', title: 'Register of laws and regulations', pageCode: 'PAF05'},
    {id: 'i1.5', kind: 'item', num: '1.5', title: 'Details of related parties', pageCode: 'PAF06'},
    {id: 'i1.5a', kind: 'item', num: '1.5a', title: 'Related parties checklist (HKAS 24)', pageCode: 'PAF06.1'},
    {id: 'i1.5b', kind: 'item', num: '1.5b', title: 'Related party transactions letter', pageCode: 'PAF06.2'},
    {id: 'i1.6', kind: 'item', num: '1.6', title: 'Significant accounting policies', pageCode: 'PAF07'},
    {id: 'i1.7', kind: 'item', num: '1.7', title: 'Accounting estimates', pageCode: 'PAF13'},
    {id: 'i1.8', kind: 'item', num: '1.8', title: 'Copy of current detailed risk assessment'},
    {id: 's2', kind: 'section', num: '2', title: 'Engagement details'},
    {id: 'i2.1', kind: 'item', num: '2.1', title: 'Letter of engagement'},
    {id: 'i2.2', kind: 'item', num: '2.2', title: 'Authorisations'},
    {id: 'i2.3', kind: 'item', num: '2.3', title: 'Special instructions from client'},
    {id: 'i2.4', kind: 'item', num: '2.4', title: 'Special instructions from group auditors'},
    {id: 'i2.5', kind: 'item', num: '2.5', title: 'Copy of resolution re: appointment'},
    {id: 'i2.6', kind: 'item', num: '2.6', title: 'New client checklist', pageCode: 'PAF08'},
    {id: 'i2.7', kind: 'item', num: '2.7', title: 'Register of non-assurance services', pageCode: 'PAF10'},
    {id: 'i2.8', kind: 'item', num: '2.8', title: 'Register of involvement in the audit', pageCode: 'PAF11'},
    {id: 's3', kind: 'section', num: '3', title: 'Accounting systems'},
    {id: 'i3.1', kind: 'item', num: '3.1', title: 'Organization chart'},
    {id: 'i3.2', kind: 'item', num: '3.2', title: 'Review of design and implementation of controls'},
    {id: 'i3.3', kind: 'item', num: '3.3', title: 'Internal Control Questionnaire'},
    {id: 'i3.4', kind: 'item', num: '3.4', title: 'Systems overview', pageCode: 'PAF09'},
    {id: 'i3.5', kind: 'item', num: '3.5', title: 'Letters of comment (copies)'},
    {id: 's4', kind: 'section', num: '4', title: 'Statutory information'},
    {id: 'i4.1', kind: 'item', num: '4.1', title: 'List of shareholders'},
    {id: 'i4.2', kind: 'item', num: '4.2', title: 'Details of mortgages/charges'},
    {id: 'i4.3', kind: 'item', num: '4.3', title: 'Directors\' interests in shares and debentures'},
    {id: 'i4.4', kind: 'item', num: '4.4', title: 'Copy annual return'},
    {id: 'i4.5', kind: 'item', num: '4.5', title: 'Copy elective resolutions'},
    {id: 'i4.6', kind: 'item', num: '4.6', title: 'Memorandum and Articles of Association'},
    {id: 's5', kind: 'section', num: '5', title: 'Taxation (if no separate tax permanent file)'},
    {id: 's6', kind: 'section', num: '6', title: 'Assets'},
    {id: 'i6.1', kind: 'item', num: '6.1', title: 'Details of freehold/leasehold properties'},
    {id: 'i6.2', kind: 'item', num: '6.2', title: 'Details of location of title deeds'},
    {id: 'i6.3', kind: 'item', num: '6.3', title: 'Details of plant etc. (where no fixed asset register exists)'},
    {id: 'i6.4', kind: 'item', num: '6.4', title: 'Details of intangible assets'},
    {id: 'i6.5', kind: 'item', num: '6.5', title: 'Investments in subsidiaries and associated undertakings'},
    {id: 'i6.6', kind: 'item', num: '6.6', title: 'Details of professional valuations'},
    {id: 'i6.7', kind: 'item', num: '6.7', title: 'Details of insurance values and cover'},
    {id: 's7', kind: 'section', num: '7', title: 'Contracts and agreements'},
    {id: 'i7.1', kind: 'item', num: '7.1', title: 'Details of contracts and agreements with index'},
    {id: 'i7.2', kind: 'item', num: '7.2', title: 'Details of share options'},
    {id: 'i7.3', kind: 'item', num: '7.3', title: 'Bank overdraft or loan facilities, security and covenants'},
    {id: 'i7.4', kind: 'item', num: '7.4', title: 'Details of other loans'},
    {id: 's8', kind: 'section', num: '8', title: 'Correspondence and information of continuing interest'},
    {id: 'i8.1', kind: 'item', num: '8.1', title: 'Index'},
    {id: 's9', kind: 'section', num: '9', title: 'Accounts'},
    {id: 'i9.1', kind: 'item', num: '9.1', title: 'Signed copies of full accounts'},
    {id: 'i9.2', kind: 'item', num: '9.2', title: 'Company accounts disclosure checklist'},
    {id: 's10', kind: 'section', num: '10', title: 'Review'},
    {id: 's11', kind: 'section', num: '11', title: 'Retention of Engagement Documentation'},
];

export type PafFormField = {
    id: string;
    label: string;
    multiline?: boolean;
    minRows?: number;
};

export type PafFormPage = {
    title: string;
    fields: PafFormField[];
};

export const PAF02_PAGE: PafFormPage = {
    title: 'BACKGROUND INFORMATION',
    fields: [
        {id: 'registeredName', label: 'Registered name of entity'},
        {id: 'tradingName', label: 'Trading name (if different)'},
        {id: 'registeredOffice', label: 'Registered office', multiline: true, minRows: 2},
        {id: 'mainPlaceOfBusiness', label: 'Main place of business (if different)', multiline: true, minRows: 2},
        {id: 'telRegistered', label: 'Tel No. (registered office)'},
        {id: 'telBusiness', label: 'Tel No. (main place of business)'},
        {id: 'faxRegistered', label: 'Fax No. (registered office)'},
        {id: 'faxBusiness', label: 'Fax No. (main place of business)'},
        {id: 'businessLocations', label: 'Number of business locations'},
        {id: 'registeredNumber', label: 'Registered number'},
        {id: 'placeOfRegistration', label: 'Place of registration'},
        {id: 'dateOfIncorporation', label: 'Date of incorporation'},
        {id: 'yearEndDate', label: 'Year end date'},
        {id: 'associatedEntities', label: 'Associated entities (Yes/No + specify)', multiline: true, minRows: 2},
        {id: 'directors', label: 'Names of directors/secretary (incl. date of birth / responsibilities)', multiline: true, minRows: 3},
        {id: 'keyPersonnel', label: 'Other key personnel / responsibilities', multiline: true, minRows: 2},
        {id: 'amlBasis', label: 'Anti money laundering — basis / documents that this is a bona-fide business', multiline: true, minRows: 3},
    ],
};

export const PAF03_PAGE: PafFormPage = {
    title: 'DETAILS OF BANKERS AND PROFESSIONAL ADVISORS',
    fields: [
        {id: 'banker1Name', label: 'Banker 1 — Name'},
        {id: 'banker1Address', label: 'Banker 1 — Address', multiline: true, minRows: 2},
        {id: 'banker1Tel', label: 'Banker 1 — Telephone'},
        {id: 'banker1Fax', label: 'Banker 1 — Fax'},
        {id: 'banker2Name', label: 'Banker 2 — Name'},
        {id: 'banker2Address', label: 'Banker 2 — Address', multiline: true, minRows: 2},
        {id: 'banker2Tel', label: 'Banker 2 — Telephone'},
        {id: 'banker2Fax', label: 'Banker 2 — Fax'},
        {id: 'signatories', label: 'Account signatories (name / specimen signature notes)', multiline: true, minRows: 3},
        {id: 'lawyerName', label: 'Lawyers — Name'},
        {id: 'lawyerAddress', label: 'Lawyers — Address', multiline: true, minRows: 2},
        {id: 'lawyerTel', label: 'Lawyers — Telephone'},
        {id: 'lawyerFax', label: 'Lawyers — Fax'},
        {id: 'otherSpecify', label: 'Other advisors (specify)'},
        {id: 'otherName', label: 'Other — Name'},
        {id: 'otherAddress', label: 'Other — Address', multiline: true, minRows: 2},
        {id: 'otherTel', label: 'Other — Telephone'},
        {id: 'otherFax', label: 'Other — Fax'},
    ],
};

export type PafLetterPage = {
    title: string;
    greeting: string;
    subject: string;
    body: string[];
    closing: string;
};

export const PAF06_2_PAGE: PafLetterPage = {
    title: 'RELATED PARTY TRANSACTIONS — SPECIMEN LETTER',
    greeting: 'Dear Sirs',
    subject: 'Related Party Transactions',
    body: [
        'As we have discussed with you, the obligations placed upon us as auditors concerning related party information have recently become more demanding.',
        'We are required by Auditing Standards to ask you as the management of the company for a complete list of parties related to this company and we have prepared the attached checklist to assist you with their identification.',
        "Please complete each question 'yes' or 'not applicable' and where answered yes provide us with a list of individuals or entities (companies, partnerships, trusts etc) as appropriate.",
        'Please contact us if you have any queries regarding the completion of the checklist.',
    ],
    closing: 'Yours faithfully',
};

export const PAF_TABLES: Partial<Record<PafSectionPageCode, AppendixTablePage>> = {
    PAF05: {
        title: 'REGISTER OF LAWS AND REGULATIONS',
        blankRows: 12,
        columns: [
            {id: 'category', label: 'Category / law'},
            {id: 'summary', label: 'Summary of the legal and regulatory framework'},
            {id: 'procedures', label: 'What procedures ensure compliance?'},
        ],
    },
    PAF06: {
        title: 'DETAILS OF RELATED PARTIES',
        blankRows: 12,
        columns: [
            {id: 'name', label: 'Name of related party'},
            {id: 'relationship', label: 'Nature of relationship'},
            {id: 'transactions', label: 'Nature of transactions'},
        ],
    },
    PAF07: {
        title: 'SIGNIFICANT ACCOUNTING POLICIES',
        blankRows: 12,
        columns: [
            {id: 'area', label: 'Area of accounts'},
            {id: 'policy', label: 'Accounting policy'},
            {id: 'evaluation', label: 'Evaluate whether appropriate and consistent with the applicable framework'},
        ],
    },
    PAF10: {
        title: 'REGISTER OF NON-ASSURANCE SERVICES FOR AUDIT CLIENTS',
        blankRows: 12,
        columns: [
            {id: 'service', label: 'Type of service'},
            {id: 'fee', label: 'Fee'},
            {id: 'threats', label: 'Threats (SI/SR/M/A/F/I)'},
            {id: 'safeguards', label: 'Safeguards required'},
        ],
    },
    PAF11: {
        title: 'REGISTER OF INVOLVEMENT IN THE AUDIT',
        blankRows: 10,
        columns: [
            {id: 'year', label: 'Year'},
            {id: 'independentPartner', label: 'Independent / other partner'},
            {id: 'auditPartner', label: 'Audit partner'},
            {id: 'manager', label: 'Manager / senior in charge'},
        ],
    },
    PAF13: {
        title: 'ACCOUNTING ESTIMATES',
        blankRows: 10,
        columns: [
            {id: 'estimate', label: 'Accounting estimate'},
            {id: 'requirements', label: 'Financial reporting / regulatory requirements and how applied'},
            {id: 'approach', label: 'Approach to making estimate'},
            {id: 'dataSources', label: 'Sources of data'},
            {id: 'uncertainty', label: 'Approach to estimation uncertainty'},
        ],
    },
};

export const PAF_PROGRAMMES: Partial<Record<PafSectionPageCode, G0Page>> = {
    PAF04: PAF04_PAGE,
    'PAF06.1': PAF06_1_PAGE,
    PAF08: PAF08_PAGE,
    PAF09: PAF09_PAGE,
};

export function getPafProgrammePage(code: string): G0Page | undefined {
    return PAF_PROGRAMMES[code as PafSectionPageCode];
}

export function getPafTablePage(code: string): AppendixTablePage | undefined {
    return PAF_TABLES[code as PafSectionPageCode];
}

export function getPafFormPage(code: string): PafFormPage | undefined {
    if (code === 'PAF02') return PAF02_PAGE;
    if (code === 'PAF03') return PAF03_PAGE;
    return undefined;
}
